import { Component, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { combineLatest, map } from 'rxjs';

import { Ga4AnalyticsService } from '../../../core/analytics/ga4-analytics.service';
import { AuthStateService } from '../../../core/auth/auth-state.service';
import { CollectionListCardComponent } from '../../../shared/ui/collection-list-card/collection-list-card.component';
import { NumeronCollectionModel } from '../models/numeron.model';
import { NumeronsService } from '../services/numerons.service';

@Component({
  selector: 'app-manage-collection-page',
  imports: [CollectionListCardComponent],
  templateUrl: './manage-collection-page.component.html',
})
export class ManageCollectionPageComponent {
  private readonly route = inject(ActivatedRoute);
  private readonly title = inject(Title);
  private readonly ga4AnalyticsService = inject(Ga4AnalyticsService);
  private readonly numeronsService = inject(NumeronsService);
  private readonly authStateService = inject(AuthStateService);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly collectionId = signal('');
  protected readonly isLoading = signal(true);
  protected readonly loadErrorMessage = signal('');
  protected readonly loadedCollection = signal<NumeronCollectionModel | null>(null);

  public constructor() {
    combineLatest([this.route.paramMap, this.route.queryParamMap])
      .pipe(
        map(([paramMap]) => ({
          collectionId: paramMap.get('collectionId') ?? '',
        })),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(({ collectionId }) => {
        this.collectionId.set(collectionId);
        this.title.setTitle('Manage Collection');
        this.ga4AnalyticsService.trackPageView({
          contentId: collectionId || undefined,
          contentType: 'Manage Collection',
          contentGroup: 'My Numerons',
        });
        this.loadCollection(collectionId);
      });
  }

  protected canManage(): boolean {
    return !this.isLoading() && this.loadErrorMessage() === '' && this.loadedCollection() !== null;
  }

  private loadCollection(collectionId: string): void {
    this.isLoading.set(true);
    this.loadErrorMessage.set('');
    this.loadedCollection.set(null);

    if (!collectionId) {
      this.loadErrorMessage.set('Collection details are unavailable right now. Please try again.');
      this.isLoading.set(false);
      return;
    }

    this.numeronsService
      .getMyNumeronCollection(collectionId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (collection) => {
          if (!collection.isCollection) {
            this.loadErrorMessage.set(
              'Collection details are unavailable right now. Please try again.',
            );
            this.isLoading.set(false);
            return;
          }

          if (!this.isPublisherMatch(collection)) {
            this.loadErrorMessage.set('You can manage only Collections published by your account.');
            this.isLoading.set(false);
            return;
          }

          this.loadedCollection.set(collection);
          this.title.setTitle(collection.title?.trim() || 'Manage Collection');
          this.isLoading.set(false);
        },
        error: () => {
          this.loadErrorMessage.set(
            'Collection details are unavailable right now. Please try again.',
          );
          this.isLoading.set(false);
        },
      });
  }

  private isPublisherMatch(collection: NumeronCollectionModel): boolean {
    const authenticatedPublisherId = this.authStateService.authUser()?.backendUserId?.trim() ?? '';
    const authenticatedDisplayName = this.authStateService.authUser()?.displayName?.trim() ?? '';
    const firstNumeron = collection.numerons[0];
    const collectionPublisherId = collection.user?._id?.trim() ?? '';
    const numeronPublisherId = firstNumeron?.user?._id?.trim() ?? '';
    const collectionPublisherName = collection.user?.displayName?.trim() ?? '';
    const numeronPublisherName = firstNumeron?.user?.displayName?.trim() ?? '';

    if (authenticatedPublisherId) {
      return (
        authenticatedPublisherId === collectionPublisherId ||
        authenticatedPublisherId === numeronPublisherId
      );
    }

    if (authenticatedDisplayName) {
      return (
        authenticatedDisplayName === collectionPublisherName ||
        authenticatedDisplayName === numeronPublisherName
      );
    }

    return true;
  }
}
